import type { Permission, PermissionDescriptor, RolePermissionMatrix } from '@/types/permission'
import type { UserRole } from '@/types/user'

export const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Proprietar',
  admin: 'Administrator',
  manager: 'Manager',
  operator: 'Operator',
  marketing: 'Marketing',
  viewer: 'Vizualizare',
}

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  owner: 'Acces complet. Rolul nu poate fi modificat sau eliminat.',
  admin: 'Acces complet la clienți, comunicare, echipă și setări.',
  manager: 'Gestionează clienții și campaniile, dar nu setările companiei.',
  operator: 'Adaugă clienți și cere acorduri — de obicei la recepție sau în magazin.',
  marketing: 'Creează campanii, trimite comunicări și vede statisticile.',
  viewer: 'Vede totul, nu poate modifica nimic.',
}

/** Order roles are listed everywhere in the UI, from most to least access. */
export const ROLE_ORDER: UserRole[] = ['owner', 'admin', 'manager', 'marketing', 'operator', 'viewer']

export const PERMISSIONS: PermissionDescriptor[] = [
  { key: 'customers.view', label: 'Vede clienții', group: 'Clienți' },
  { key: 'customers.create', label: 'Adaugă clienți', group: 'Clienți' },
  { key: 'customers.edit', label: 'Modifică datele clienților', group: 'Clienți' },
  { key: 'customers.archive', label: 'Arhivează clienți', group: 'Clienți' },
  { key: 'customers.import', label: 'Importă liste de clienți', group: 'Clienți' },
  { key: 'consents.request', label: 'Cere acorduri și semnături', group: 'Acorduri' },
  { key: 'documents.view', label: 'Vede documentele', group: 'Acorduri' },
  { key: 'messages.send', label: 'Trimite mesaje individuale', group: 'Comunicare' },
  { key: 'campaigns.view', label: 'Vede campaniile și mesajele', group: 'Comunicare' },
  { key: 'campaigns.create', label: 'Creează și trimite campanii', group: 'Comunicare' },
  { key: 'templates.manage', label: 'Creează și modifică șabloane', group: 'Comunicare' },
  { key: 'segments.manage', label: 'Creează grupuri de clienți', group: 'Comunicare' },
  { key: 'automations.manage', label: 'Configurează automatizări', group: 'Comunicare' },
  { key: 'analytics.view', label: 'Vede statisticile', group: 'Statistici' },
  { key: 'audit.view', label: 'Vede jurnalul de activitate', group: 'Companie' },
  { key: 'team.manage', label: 'Invită colegi și schimbă roluri', group: 'Companie' },
  { key: 'settings.manage', label: 'Modifică setările companiei', group: 'Companie' },
]

export const ALL_PERMISSIONS: Permission[] = PERMISSIONS.map((p) => p.key)

const VIEW_ONLY: Permission[] = ['customers.view', 'documents.view', 'campaigns.view', 'analytics.view']

/**
 * Defaults straight from the spec (§20): Operator adds clients and asks for
 * consents but never touches company settings; Marketing runs campaigns and
 * reads analytics; Admin/Owner do everything. Owners/admins can tune these
 * from Echipă → Roluri; the backend will hold the authoritative copy.
 */
export const DEFAULT_ROLE_PERMISSIONS: RolePermissionMatrix = {
  owner: [...ALL_PERMISSIONS],
  admin: [...ALL_PERMISSIONS],
  manager: ALL_PERMISSIONS.filter((p) => !['settings.manage'].includes(p)),
  marketing: [
    ...VIEW_ONLY,
    'messages.send',
    'campaigns.create',
    'templates.manage',
    'segments.manage',
    'automations.manage',
  ],
  operator: ['customers.view', 'customers.create', 'customers.edit', 'consents.request', 'documents.view', 'messages.send'],
  viewer: [...VIEW_ONLY],
}

/** Roles whose permission set is fixed — shown locked in the matrix. */
export const LOCKED_ROLES: UserRole[] = ['owner']
